import { useEffect, useState } from 'react';
import { Alert, Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { getSettlementConfig, updateSettlementConfig } from '../api/financeApi';
import InfoTip from '../components/InfoTip';
import { useFinance } from '../financeContext';
import { SETTLEMENT_CONFIG_FIELDS, VERTICALS, VERTICAL_LABEL } from '../wording';

// §A9 — render every settlement-config flag with a humanised label, unit adornment, an InfoTip
// and a one-line <Form.Text muted>. Includes ALL fields the schema supports (fixes the old
// real_estate fork that dropped deliveryAssignmentSlaHours + realEstateVisitFee), plus the two
// new v6 fields and the forfeit-tier editor.

function numFromInput(v) {
  return v === '' || v == null ? '' : v;
}

function ScalarField({ field, value, disabled, onChange }) {
  const isRate = field.kind === 'rate';
  return (
    <Form.Group className="mb-3">
      <Form.Label className="mb-1">
        {field.label}
        <InfoTip text={field.help} />
        {field.appliesTo && field.appliesTo !== 'all' && (
          <span className="text-muted small ms-2">({field.appliesTo})</span>
        )}
      </Form.Label>
      <InputGroup size="sm" style={{ maxWidth: 320 }}>
        <Form.Control
          type="number"
          step={isRate ? '0.01' : '1'}
          min="0"
          max={isRate ? '1' : undefined}
          disabled={disabled}
          value={numFromInput(value)}
          onChange={(e) => onChange(e.target.value)}
        />
        {field.unit && <InputGroup.Text>{field.unit}</InputGroup.Text>}
      </InputGroup>
      <Form.Text muted>{field.help}</Form.Text>
    </Form.Group>
  );
}

function PerVerticalHours({ field, value = {}, disabled, onChange }) {
  return (
    <Form.Group className="mb-3">
      <Form.Label className="mb-1">
        {field.label} <InfoTip text={field.help} />
      </Form.Label>
      <div className="row g-2" style={{ maxWidth: 560 }}>
        {VERTICALS.map((v) => (
          <div className="col-6 col-md-3" key={v}>
            <Form.Label className="small mb-0">{VERTICAL_LABEL[v]}</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                type="number" min="0" disabled={disabled}
                value={numFromInput(value?.[v])}
                onChange={(e) => onChange({ ...(value || {}), [v]: e.target.value === '' ? '' : Number(e.target.value) })}
              />
              <InputGroup.Text>h</InputGroup.Text>
            </InputGroup>
          </div>
        ))}
      </div>
      <Form.Text muted>{field.help}</Form.Text>
    </Form.Group>
  );
}

function ForfeitTiersEditor({ field, tiers = [], disabled, onChange }) {
  const rows = Array.isArray(tiers) ? tiers : [];
  const set = (i, key, val) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val === '' ? '' : Number(val) } : r)));
  return (
    <Form.Group className="mb-3">
      <Form.Label className="mb-1">{field.label} <InfoTip text={field.help} /></Form.Label>
      <Form.Text muted className="d-block mb-2">{field.help}</Form.Text>
      {rows.length === 0 && <div className="small text-muted mb-2">No tiers — the max forfeit rate applies flat.</div>}
      {rows.map((r, i) => (
        <div className="d-flex align-items-center gap-2 mb-1" key={i}>
          <InputGroup size="sm" style={{ maxWidth: 190 }}>
            <Form.Control type="number" min="0" placeholder="days before start" disabled={disabled}
              value={numFromInput(r.daysBeforeStart)} onChange={(e) => set(i, 'daysBeforeStart', e.target.value)} />
            <InputGroup.Text>days</InputGroup.Text>
          </InputGroup>
          <InputGroup size="sm" style={{ maxWidth: 150 }}>
            <Form.Control type="number" step="0.01" min="0" max="1" placeholder="forfeit" disabled={disabled}
              value={numFromInput(r.forfeitRate)} onChange={(e) => set(i, 'forfeitRate', e.target.value)} />
            <InputGroup.Text>rate</InputGroup.Text>
          </InputGroup>
          {!disabled && (
            <Button size="sm" variant="outline-danger" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>×</Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button size="sm" variant="outline-secondary" className="mt-1"
          onClick={() => onChange([...rows, { daysBeforeStart: 0, forfeitRate: 0 }])}>
          + Add tier
        </Button>
      )}
    </Form.Group>
  );
}

export default function SettlementConfigPage() {
  const { canEditConfig } = useFinance();
  const [config, setConfig] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettlementConfig().then(setConfig).catch(() => setLoadErr('Could not load settlement configuration.'));
  }, []);

  const setField = (key, val) => setConfig((p) => ({ ...p, [key]: val }));

  const save = async (e) => {
    e.preventDefault();
    setMsg(''); setErr(''); setSaving(true);
    try {
      const payload = {};
      SETTLEMENT_CONFIG_FIELDS.forEach((f) => {
        const v = config[f.key];
        if (f.kind === 'perVerticalHours') {
          payload[f.key] = Object.fromEntries(
            Object.entries(v || {}).map(([k, val]) => [k, val === '' ? undefined : Number(val)])
          );
        } else if (f.kind === 'tiers') {
          payload[f.key] = (Array.isArray(v) ? v : [])
            .filter((r) => r.daysBeforeStart !== '' && r.forfeitRate !== '')
            .map((r) => ({ daysBeforeStart: Number(r.daysBeforeStart), forfeitRate: Number(r.forfeitRate) }))
            .sort((a, b) => b.daysBeforeStart - a.daysBeforeStart);
        } else if (v !== '' && v != null) {
          payload[f.key] = Number(v);
        }
      });
      const updated = await updateSettlementConfig(payload);
      setConfig(updated);
      setMsg('Settlement configuration saved.');
    } catch (e2) {
      setErr(e2?.response?.data?.msg || 'Unable to update settlement configuration.');
    } finally {
      setSaving(false);
    }
  };

  if (loadErr) return <Alert variant="danger">{loadErr}</Alert>;
  if (!config) return <Spinner animation="border" size="sm" role="status" />;

  return (
    <Form onSubmit={save} style={{ maxWidth: 720 }}>
      <h5>Settlement configuration <span className="text-muted small">(platform-wide)</span></h5>
      {!canEditConfig && <Alert variant="light" className="py-2 small">View-only — only an admin or finance manager can save changes.</Alert>}
      {msg && <Alert variant="success" className="py-2">{msg}</Alert>}
      {err && <Alert variant="danger" className="py-2">{err}</Alert>}

      {SETTLEMENT_CONFIG_FIELDS.map((f) => {
        if (f.kind === 'perVerticalHours') {
          return <PerVerticalHours key={f.key} field={f} value={config[f.key]} disabled={!canEditConfig}
            onChange={(v) => setField(f.key, v)} />;
        }
        if (f.kind === 'tiers') {
          return <ForfeitTiersEditor key={f.key} field={f} tiers={config[f.key]} disabled={!canEditConfig}
            onChange={(v) => setField(f.key, v)} />;
        }
        return <ScalarField key={f.key} field={f} value={config[f.key]} disabled={!canEditConfig}
          onChange={(v) => setField(f.key, v)} />;
      })}

      {canEditConfig && <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save configuration'}</Button>}
    </Form>
  );
}
